import { useChats } from '~/features/whatsapp/hooks/useChats';
import { useResolvedChatEntryState } from '~/features/whatsapp/hooks/useResolvedChatEntryState';
import { CHAT_ENTRY_STATES, type ChatEntryState } from '~/features/whatsapp/utils/resolveChatEntryState';
import { useRepresentedUsers } from '~/hooks/useRepresentedUser';
import useUserInfo from '~/hooks/useUserInfo';

type Options = {
    // opt-in: with the lead's assignees the entry is also gated like the backend's startChat (assigned or delegate)
    assignedUserIds?: string[];
};

// only a visible chat of this lead keeps the entry enabled without a number
export const useLeadChatEntryState = (
    loanRequestId: string | undefined,
    { assignedUserIds }: Options = {},
): ChatEntryState => {
    const { data: chats, error: chatsError, isPending: isChatsPending } = useChats();
    const user = useUserInfo();
    const { data: representedUsers, isPending: isRepresentedPending } = useRepresentedUsers(
        assignedUserIds ? user?.id : undefined,
    );

    const hasExistingChats = (() => {
        if (isChatsPending || !loanRequestId) return undefined;
        if (!chats) return null;
        return chats.some((chat) => chat.loanRequestIds.includes(loanRequestId));
    })();

    const state = useResolvedChatEntryState(hasExistingChats, chatsError);
    if (!assignedUserIds || state !== CHAT_ENTRY_STATES.ENABLED || !user) return state;

    const isAssigned =
        assignedUserIds.includes(user.id) ||
        !!representedUsers?.some((represented) => assignedUserIds.includes(represented.id));
    // represented users decide delegate access, so don't flip to disabled while they load
    if (!isAssigned && isRepresentedPending) return state;

    return isAssigned ? state : CHAT_ENTRY_STATES.NOT_ASSIGNED;
};
